// HERRAMIENTAS EN EL TELÉFONO, a pantalla completa
// (ADR-2026-10-09-herramientas-en-el-telefono-a-pantalla-completa). La abre la pestaña
// Herramientas de la barra de abajo; antes era una lista de renglones en el sheet de
// destinos (`ListaDeHerramientas`, que sigue en la home).
//
// Arriba, las cuatro principales —Estudio de Video, Animar imagen, Carrusel y Editar— a todo
// el ancho; desde la quinta, de a dos por fila. Cada card lleva la carátula de su modo o de
// su atajo y una explicación de qué hace. Qué va y en qué orden lo arma
// `herramientasDeLaPantalla`; acá sólo se dibuja.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { EdicionSheet, ModoSheet } from './SheetGenerar';
import { setupAutoplayVideo } from './HomeCards';
import { ALTO_NAV_INFERIOR } from './NavInferiorMobile';
import { herramientasDeLaPantalla, type HerramientaMobile } from './herramientasMobile';

export function PantallaDeHerramientas({
  ediciones,
  modos,
  onElegir,
}: {
  ediciones: EdicionSheet[];
  /** Los modos de Herramientas, ya en el orden de la sidebar. */
  modos: ModoSheet[];
  /** Abre la herramienta y cierra la pantalla (también con candado: lleva a planes). */
  onElegir: (onAbrir: () => void) => void;
}) {
  // Entra subiendo desde abajo, como el sheet que reemplaza, pero ocupa la pantalla entera.
  const [adentro, setAdentro] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setAdentro(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const { principales, resto } = herramientasDeLaPantalla(ediciones, modos);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Herramientas"
      // Termina ARRIBA de la barra de abajo, que queda a la vista como en Creaciones
      // (ADR-2026-10-09-herramientas-en-el-telefono-a-pantalla-completa-3).
      className={`fixed inset-x-0 top-0 z-[70] overflow-y-auto overscroll-contain bg-[#F7F7FA] transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none md:hidden ${
        adentro ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
      style={{ bottom: ALTO_NAV_INFERIOR, paddingBottom: 24 }}
    >
      {/* Cabecera fija: el título se queda mientras se recorre la lista. */}
      <div
        className="sticky top-0 z-10 bg-[#F7F7FA]/90 px-4 pb-3 backdrop-blur-md"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        {/* Sin cerrar: es una pantalla de la barra, como Creaciones, y se sale tocando otro
            lugar de abajo (ADR-2026-10-09-creaciones-sube-a-la-altura-de-herramientas). */}
        <div className="min-w-0 pt-1">
          <h1 className="text-[26px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">Herramientas</h1>
          <p className="mt-0.5 text-[13.5px] leading-snug text-black/50">
            Editá, animá y terminá tus fotos y videos.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 pt-1">
        {principales.map((h, i) => (
          <TarjetaGrande key={h.id} herramienta={h} orden={i} onElegir={onElegir} />
        ))}
      </div>

      {resto.length > 0 && (
        <>
          <h2 className="px-5 pb-2.5 pt-6 text-[13px] font-semibold uppercase tracking-[0.06em] text-black/40">
            Más herramientas
          </h2>
          <div className="grid grid-cols-2 gap-3 px-4">
            {resto.map((h, i) => (
              <TarjetaChica key={h.id} herramienta={h} orden={principales.length + i} onElegir={onElegir} />
            ))}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}

/** Cada card entra apenas después de la anterior: la pantalla se arma de arriba abajo. */
const entrada = (orden: number) => ({ animationDelay: `${80 + Math.min(orden, 10) * 35}ms` });

/**
 * La imagen de la herramienta, con el lenguaje del abanico de las cards por destino de la
 * home (`AbanicoDeResultados`): una FICHA inclinada —borde blanco, esquinas redondeadas,
 * sombra— apoyada sobre el tinte. Las ilustraciones de los atajos (fondo transparente) ya
 * vienen compuestas como fichas, así que van sueltas, sin marco.
 *
 * `ancho` y `alto` son los de la ficha, en px; `giro`, su inclinación.
 */
function Ficha({
  herramienta: h,
  ancho,
  alto,
  giro,
}: {
  herramienta: HerramientaMobile;
  ancho: number;
  alto: number;
  giro: number;
}) {
  const { medio } = h;
  if (medio.tipo === 'imagen' && medio.ajuste === 'contain') {
    if (!medio.src) return null;
    // Del tamaño de la ficha (la ilustración trae su propio aire transparente) y con la mitad
    // del giro: ya viene compuesta, sólo acompaña la inclinación del resto.
    return (
      <img
        src={medio.src}
        alt=""
        loading="lazy"
        className="block object-contain drop-shadow-[0_8px_14px_rgba(16,24,45,0.14)]"
        style={{ width: ancho, height: alto, transform: `rotate(${giro / 2}deg)` }}
      />
    );
  }
  return (
    <span
      className="relative block overflow-hidden rounded-[14px] bg-white shadow-[0_10px_24px_rgba(16,24,45,0.18)] ring-[3px] ring-white"
      style={{ width: ancho, height: alto, transform: `rotate(${giro}deg)` }}
    >
      {medio.tipo === 'dibujo' ? (
        // La carátula dibujada (Estudio) tiene tipografía fija: se dibuja al doble y se
        // achica, así se ve igual que en grande en vez de romper renglones.
        <span
          className="absolute left-0 top-0 block origin-top-left"
          style={{ width: ancho * 2, height: alto * 2, transform: 'scale(0.5)' }}
        >
          <medio.Dibujo />
        </span>
      ) : medio.tipo === 'video' ? (
        <video
          ref={setupAutoplayVideo}
          src={medio.src}
          poster={medio.poster}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <img src={medio.src} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      )}
    </span>
  );
}

/** El candado del plan o "en computadora": una pastilla blanca, como "Próximamente" en la
 *  card de Anuncios de la home. */
function Sello({ herramienta: h }: { herramienta: HerramientaMobile }) {
  if (!h.bloqueado && !h.soloEnComputadora) return null;
  return (
    <span className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-white py-0.5 pl-1.5 pr-2.5 text-[11.5px] font-bold text-[#16181D]">
      <span className="material-symbols-rounded text-[14px]">{h.soloEnComputadora ? 'computer' : 'lock'}</span>
      {h.soloEnComputadora ? 'En computadora' : 'Planes pagos'}
    </span>
  );
}

/**
 * Las principales: la card apaisada de destino de la home (`CardDestinoMobile`) —tinte,
 * texto a la izquierda, la imagen inclinada a la derecha—. Alternan el giro para que la
 * columna no se lea como una plantilla repetida.
 */
function TarjetaGrande({
  herramienta: h,
  orden,
  onElegir,
}: {
  herramienta: HerramientaMobile;
  orden: number;
  onElegir: (onAbrir: () => void) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onElegir(h.onAbrir)}
      style={{ ...entrada(orden), background: h.tinte }}
      className="animate-ficha-de-pestana group relative flex min-h-[124px] w-full items-center overflow-hidden rounded-[20px] border border-black/[0.06] px-4 py-3.5 text-left transition-transform duration-200 active:scale-[0.99]"
    >
      <span className="relative z-10 flex w-[56%] flex-col">
        <span className="text-[17px] font-bold leading-tight tracking-[-0.02em] text-[#16181D]">{h.nombre}</span>
        <span className="mt-0.5 text-[12.5px] leading-snug text-black/55">{h.explicacion}</span>
        <Sello herramienta={h} />
      </span>
      {/* Corrida afuera, como el abanico de la card de destino: se corta en el borde y deja
          más lugar al texto. */}
      <span className="pointer-events-none absolute right-[-14px] top-1/2 -translate-y-1/2">
        <Ficha herramienta={h} ancho={128} alto={96} giro={orden % 2 === 0 ? 5 : -5} />
      </span>
    </button>
  );
}

/** El resto, de a dos: la misma card con la imagen arriba y el texto abajo. */
function TarjetaChica({
  herramienta: h,
  orden,
  onElegir,
}: {
  herramienta: HerramientaMobile;
  orden: number;
  onElegir: (onAbrir: () => void) => void;
}) {
  return (
    <button
      type="button"
      onClick={h.soloEnComputadora ? undefined : () => onElegir(h.onAbrir)}
      disabled={h.soloEnComputadora}
      style={{ ...entrada(orden), background: h.tinte }}
      className="animate-ficha-de-pestana group relative flex min-w-0 flex-col overflow-hidden rounded-[18px] border border-black/[0.06] text-left transition-transform duration-200 active:scale-[0.97] disabled:active:scale-100"
    >
      <span className={`flex h-[118px] items-center justify-center ${h.soloEnComputadora ? 'opacity-60' : ''}`}>
        <Ficha herramienta={h} ancho={112} alto={84} giro={orden % 2 === 0 ? -4 : 4} />
      </span>
      <span className="flex flex-col px-3 pb-3.5">
        <span className={`text-[15px] font-bold leading-tight tracking-[-0.01em] ${h.soloEnComputadora ? 'text-black/45' : 'text-[#16181D]'}`}>
          {h.nombre}
        </span>
        {/* Tres líneas como mucho: los textos están escritos para entrar, y esto es el
            respaldo si un texto nuevo se estira. */}
        <span
          className="mt-1 text-[12px] leading-snug text-black/55"
          style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {h.explicacion}
        </span>
        <Sello herramienta={h} />
      </span>
    </button>
  );
}
